import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Building2, QrCode, Upload, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

const PaymentPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'file'
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentConfigs, setPaymentConfigs] = useState([]);
  const [hourlyPricingRules, setHourlyPricingRules] = useState([]);

  useEffect(() => {
    loadBookingDetails();
    loadPaymentConfigs();
  }, [bookingId]);

  useEffect(() => {
    const venueId = bookingDetails?.booking?.venue_id;
    if (venueId) loadHourlyPricingRules(venueId);
  }, [bookingDetails]);

  const loadPaymentConfigs = async () => {
    try {
      const response = await api.get('/payment-config');
      const list = Array.isArray(response.data) ? response.data : [];
      setPaymentConfigs(list);
      const hasQris = list.some((c) => String(c.bank_name || '').toUpperCase() === 'QRIS' && c.is_active);
      const hasBank = list.some((c) => String(c.bank_name || '').toUpperCase() !== 'QRIS' && c.is_active);
      if (!paymentMethod) {
        if (hasQris) setPaymentMethod('qris');
        else if (hasBank) setPaymentMethod('transfer');
        else setPaymentMethod('');
      }
    } catch (error) {
      console.error('Failed to load payment configs');
      setPaymentConfigs([]);
      if (!paymentMethod) setPaymentMethod('');
    }
  };

  const loadBookingDetails = async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      setBookingDetails(response.data);
    } catch (error) {
      toast.error('Gagal memuat data booking');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadHourlyPricingRules = async (venueId) => {
    try {
      const response = await api.get('/settings/public');
      const rawMap = response.data?.venue_hourly_pricing_rules;
      const raw = response.data?.hourly_pricing_rules;
      let rulesForVenue = null;
      if (rawMap) {
        try {
          const parsedMap = JSON.parse(rawMap);
          if (parsedMap && typeof parsedMap === 'object') {
            const list = parsedMap?.[venueId];
            if (Array.isArray(list)) rulesForVenue = list;
          }
        } catch {}
      }
      if (rulesForVenue) {
        setHourlyPricingRules(rulesForVenue);
      } else if (raw) {
        const parsed = JSON.parse(raw);
        setHourlyPricingRules(Array.isArray(parsed) ? parsed : []);
      } else {
        setHourlyPricingRules([]);
      }
    } catch {
      setHourlyPricingRules([]);
    }
  };

  const getDayType = (dateStr) => {
    const dt = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
    if (!dt || Number.isNaN(dt.getTime())) return 'weekday';
    const day = dt.getDay(); // 0=Sun,6=Sat
    return day === 0 || day === 6 ? 'weekend' : 'weekday';
  };

  const getHourlyRate = (hour, basePricePerHour, dayType) => {
    const rules = Array.isArray(hourlyPricingRules) ? hourlyPricingRules : [];
    const day = String(dayType || 'all').toLowerCase();
    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour < 0) continue;
      if (ruleDayType === day && hour >= startHour && hour < endHour) return pricePerHour;
    }
    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour < 0) continue;
      if (ruleDayType === 'all' && hour >= startHour && hour < endHour) return pricePerHour;
    }
    return basePricePerHour;
  };

  const calculateTotalAmount = (venuePricePerHour, timeSlots, bookingDate) => {
    const slots = Array.isArray(timeSlots) ? timeSlots : [];
    if (slots.length === 0) return venuePricePerHour || 0;
    const dayType = getDayType(bookingDate);
    return slots.reduce((sum, slot) => {
      const match = typeof slot === 'string' ? slot.match(/^(\d{1,2}):/) : null;
      const hour = match ? Number(match[1]) : null;
      const rate = Number.isInteger(hour) ? getHourlyRate(hour, venuePricePerHour || 0, dayType) : (venuePricePerHour || 0);
      return sum + rate;
    }, 0);
  };

  const hasActiveQris = paymentConfigs.some((c) => String(c.bank_name || '').toUpperCase() === 'QRIS' && c.is_active);
  const hasActiveBank = paymentConfigs.some((c) => String(c.bank_name || '').toUpperCase() !== 'QRIS' && c.is_active);

  const handlePayment = async () => {
    if (!paymentMethod) {
      toast.error('Metode pembayaran belum tersedia. Hubungi admin.');
      return;
    }
    if (uploadMethod === 'url' && !proofUrl.trim()) {
      toast.error('Silakan masukkan URL bukti pembayaran');
      return;
    }
    if (uploadMethod === 'file' && !proofFile) {
      toast.error('Silakan upload file bukti pembayaran');
      return;
    }

    setSubmitting(true);
    try {
      // Create payment
      const paymentResponse = await api.post('/payments', {
        booking_id: bookingId,
        method: paymentMethod
      });

      // Upload proof
      if (uploadMethod === 'url') {
        await api.post(`/payments/${paymentResponse.data.id}/upload?proof_url=${encodeURIComponent(proofUrl)}`);
      } else {
        // Upload file
        const formData = new FormData();
        formData.append('file', proofFile);
        await api.post(`/payments/${paymentResponse.data.id}/upload-file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success('Pembayaran berhasil disubmit! Menunggu verifikasi admin');
      navigate(`/confirmation/${bookingId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memproses pembayaran');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const booking = bookingDetails?.booking;
  const venue = bookingDetails?.venue;
  const fallbackNormal = calculateTotalAmount(venue?.price_per_hour || 0, booking?.time_slots, booking?.booking_date);
  const normalAmount =
    Number(booking?.normal_amount) > 0 ? Number(booking.normal_amount) : Number(fallbackNormal || 0);
  const discountAmount = Number(booking?.discount_amount) > 0 ? Number(booking.discount_amount) : 0;
  const discountSource = String(booking?.discount_source || 'none');
  const totalPayable = Math.max(0, normalAmount - discountAmount);
  const discountSourceLabel =
    discountSource === 'member' ? 'Membership' : discountSource === 'voucher' ? 'Voucher' : '-';

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Zero Futsal</span>
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Kembali</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-2">Pembayaran</h1>
            <p className="text-zinc-400">Selesaikan pembayaran untuk konfirmasi booking</p>
          </div>

          {/* Booking Summary */}
          <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4 uppercase font-heading">Ringkasan Pesanan</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-zinc-400">Lapangan</div>
                <div className="text-white font-bold">{venue?.name}</div>
              </div>
              <div>
                <div className="text-sm text-zinc-400">Tipe</div>
                <div className="text-white font-bold">{venue?.type}</div>
              </div>
              <div>
                <div className="text-sm text-zinc-400">Tanggal</div>
                <div className="text-white font-bold">
                  {booking?.booking_date}
                </div>
              </div>
              <div>
                <div className="text-sm text-zinc-400">Jam</div>
                <div className="text-white font-bold">
                  {Array.isArray(booking?.time_slots)
                    ? booking.time_slots.length > 1
                      ? `${booking.time_slots.length} slot`
                      : booking.time_slots[0]
                    : booking?.time_slot || '-'}
                </div>
                {Array.isArray(booking?.time_slots) && booking.time_slots.length > 1 && (
                  <div className="text-xs text-zinc-500 mt-1">
                    {booking.time_slots.join(', ')}
                  </div>
                )}
              </div>
              <div className="col-span-2 border-t border-zinc-700 pt-4 mt-2">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="text-sm text-zinc-400">Jumlah jam</div>
                  <div className="text-white text-right">
                    {Array.isArray(booking?.time_slots) ? booking.time_slots.length : 1} jam
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="text-sm text-zinc-400">Harga normal</div>
                  <div className="text-white text-right font-semibold">
                    Rp {Number(normalAmount || 0).toLocaleString('id-ID')}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="text-sm text-zinc-400">Diskon</div>
                  <div className="text-white text-right font-semibold">
                    {discountAmount > 0 ? `- Rp ${Number(discountAmount || 0).toLocaleString('id-ID')}` : '-'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="text-sm text-zinc-400">Sumber diskon</div>
                  <div className="text-white text-right">{discountSourceLabel}</div>
                </div>
                <div className="text-sm text-zinc-400 mb-1">Total Pembayaran</div>
                <div className="text-3xl font-black text-primary">
                  Rp {Number(totalPayable || 0).toLocaleString('id-ID')}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-6 uppercase font-heading">Pilih Metode Pembayaran</h2>
            {!hasActiveQris && !hasActiveBank ? (
              <div className="text-sm text-zinc-400">Metode pembayaran belum dikonfigurasi admin.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {hasActiveQris && (
                  <button
                    data-testid="payment-method-qris"
                    onClick={() => setPaymentMethod('qris')}
                    className={`p-6 rounded-lg border-2 transition-all flex flex-col items-center gap-3 ${
                      paymentMethod === 'qris'
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-zinc-800 border-zinc-700 text-white hover:border-primary/50'
                    }`}
                  >
                    <QrCode className="w-12 h-12" />
                    <div className="font-bold text-lg">QRIS</div>
                    <div className="text-sm text-zinc-400">Scan QR Code</div>
                  </button>
                )}

                {hasActiveBank && (
                  <button
                    data-testid="payment-method-transfer"
                    onClick={() => setPaymentMethod('transfer')}
                    className={`p-6 rounded-lg border-2 transition-all flex flex-col items-center gap-3 ${
                      paymentMethod === 'transfer'
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-zinc-800 border-zinc-700 text-white hover:border-primary/50'
                    }`}
                  >
                    <Building2 className="w-12 h-12" />
                    <div className="font-bold text-lg">Transfer Bank</div>
                    <div className="text-sm text-zinc-400">Transfer manual</div>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Payment Details */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-6 uppercase font-heading">Detail Pembayaran</h2>
            
            {paymentMethod === 'qris' ? (
              <div className="text-center">
                {paymentConfigs.filter(c => String(c.bank_name || '').toUpperCase() === 'QRIS' && c.is_active).length > 0 ? (
                  paymentConfigs.filter(c => String(c.bank_name || '').toUpperCase() === 'QRIS' && c.is_active).map(config => (
                    <div key={config.id}>
                      <div className="bg-white p-6 rounded-lg inline-block mb-4">
                        <img
                          src={config.qris_image_url}
                          alt="QRIS Code"
                          className="w-48 h-48"
                          onError={(e) => {
                            e.target.src = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=ZEROFUTSAL_DEMO_QR";
                          }}
                        />
                      </div>
                      <p className="text-zinc-400 text-sm">Scan QR Code di atas dengan aplikasi e-wallet Anda</p>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-6 rounded-lg inline-block mb-4">
                    <img
                      src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=ZEROFUTSAL_DEMO_QR"
                      alt="QRIS Code"
                      className="w-48 h-48"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {paymentConfigs.filter(c => String(c.bank_name || '').toUpperCase() !== 'QRIS' && c.is_active).length > 0 ? (
                  paymentConfigs.filter(c => String(c.bank_name || '').toUpperCase() !== 'QRIS' && c.is_active).map(config => (
                    <div key={config.id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                      <div className="text-sm text-zinc-400 mb-2">Bank {config.bank_name}</div>
                      <div className="text-2xl font-mono font-bold text-primary mb-1">{config.account_number}</div>
                      <div className="text-sm text-zinc-300">a.n. {config.account_holder}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-zinc-400">Rekening bank belum dikonfigurasi admin.</div>
                )}
              </div>
            )}
          </div>

          {/* Upload Proof */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-6 uppercase font-heading flex items-center gap-3">
              <Upload className="w-6 h-6 text-primary" />
              Upload Bukti Pembayaran
            </h2>
            
            {/* Upload method selection */}
            <div className="mb-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setUploadMethod('url')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    uploadMethod === 'url'
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'bg-zinc-800 border-zinc-700 text-white hover:border-primary/50'
                  }`}
                >
                  <div className="font-bold">URL Gambar</div>
                  <div className="text-xs mt-1 opacity-70">Paste link gambar</div>
                </button>
                <button
                  onClick={() => setUploadMethod('file')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    uploadMethod === 'file'
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'bg-zinc-800 border-zinc-700 text-white hover:border-primary/50'
                  }`}
                >
                  <div className="font-bold">Upload File</div>
                  <div className="text-xs mt-1 opacity-70">Upload dari device</div>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {uploadMethod === 'url' ? (
                <div>
                  <label className="block text-sm font-semibold text-zinc-300 mb-2">
                    URL Gambar Bukti Transfer
                  </label>
                  <input
                    data-testid="proof-url-input"
                    type="url"
                    value={proofUrl}
                    onChange={(e) => setProofUrl(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                    placeholder="https://example.com/bukti-transfer.jpg"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    Upload gambar ke hosting (imgur, postimages, dll) dan paste URL di sini
                  </p>
                  {proofUrl && (
                    <div className="bg-zinc-800 rounded-lg p-4 mt-4">
                      <p className="text-sm text-zinc-400 mb-2">Preview:</p>
                      <img
                        src={proofUrl}
                        alt="Bukti pembayaran"
                        className="max-w-full h-auto rounded border border-zinc-700"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          toast.error('URL gambar tidak valid');
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-zinc-300 mb-2">
                    Upload File Gambar
                  </label>
                  <div className="relative">
                    <input
                      data-testid="proof-file-input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Ukuran file maksimal 5MB');
                            e.target.value = '';
                            return;
                          }
                          setProofFile(file);
                        }
                      }}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary text-white rounded-md px-4 py-3 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-black hover:file:bg-primary-hover cursor-pointer outline-none"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Format: JPG, PNG, WebP (Max 5MB)
                  </p>
                  {proofFile && (
                    <div className="bg-zinc-800 rounded-lg p-4 mt-4">
                      <p className="text-sm text-zinc-400 mb-2">File terpilih:</p>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-zinc-700 rounded-lg flex items-center justify-center">
                          <Upload className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                          <p className="text-white font-bold">{proofFile.name}</p>
                          <p className="text-xs text-zinc-500">{(proofFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      {proofFile.type.startsWith('image/') && (
                        <img
                          src={URL.createObjectURL(proofFile)}
                          alt="Preview"
                          className="mt-4 max-w-full h-auto rounded border border-zinc-700"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            data-testid="confirm-payment-btn"
            onClick={handlePayment}
            disabled={submitting || (uploadMethod === 'url' ? !proofUrl : !proofFile)}
            className="w-full bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-8 py-4 font-bold uppercase tracking-wider transition-all duration-200 shadow-[4px_4px_0px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_0px_#FFFFFF] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            <span>{submitting ? 'Memproses...' : 'Konfirmasi Pembayaran'}</span>
            {!submitting && <Check className="w-6 h-6" />}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentPage;
